import React from 'react';

import { useResponsive, useTranslation } from 'client/hooks';

import { ControlPanel, Header } from 'client/components/common/mobile';
import { HelmetComponent, Icon } from 'client/components/common';
import aboutBgD from 'client/assets/about/about-bg__d-2.jpg';
import aboutBgM from 'client/assets/about/about-bg__m.png';
import storyBgD from 'client/assets/about/story-bg__d.png';
import storyBgM from 'client/assets/about/story-bg__m-new.png';
import storyBgFold from 'client/assets/about/story-bg-fold.png';
import valuesBgD from 'client/assets/about/values-bg__d.png';
import valuesBgM from 'client/assets/about/values-bg__m.png';
import cukiermanLogo from 'client/assets/about/cukierman-logo.svg';
import catalistLogo from 'client/assets/about/catalist-logo.svg';
import { BOARD, TEAM } from 'client/constants';

import css from './About.module.scss';

const About = () => {
  const [isMobile] = useResponsive(['MOBILE']);
  const { translate } = useTranslation();

  const onScrollToClick = () => {
    const scrolledEl = document.getElementById('story-section');
    if (scrolledEl) {
      scrolledEl.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <>
      <HelmetComponent
        title={translate('seo.about.title')}
        description={translate('seo.about.description')}
        ogTitle={translate('seo.about.title')}
        ogDescription={translate('seo.about.description')}
      ></HelmetComponent>
      <div className={css.pageWrapper}>
        {isMobile ? <Header className={css.pageHeader} /> : null}
        {isMobile ? <ControlPanel className={css.controlPanel} /> : null}

        <div className={css.contentWrapper}>
          <section className={css.about} style={{ backgroundImage: `url(${isMobile ? aboutBgM : aboutBgD})` }}>
            <div className={css.content}>
              <h1 className={css.mainTitle}>{translate('aboutUs.aboutUsTitle')}</h1>
              <p dangerouslySetInnerHTML={{ __html: translate('aboutUs.aboutUsText') }} />
            </div>
            <button type="button" className={css.scrollDownBtn} onClick={onScrollToClick}>
              <Icon type="scroll-down" />
            </button>
          </section>
          <section className={css.story} id="story-section">
            <div className={css.content}>
              <h2 className={css.secondaryTitle}>{translate('aboutUs.ourStoryTitle')}</h2>
              <p dangerouslySetInnerHTML={{ __html: translate('aboutUs.ourStoryText1') }} />
              <p dangerouslySetInnerHTML={{ __html: translate('aboutUs.ourStoryText2') }} />
            </div>
            <img src={isMobile ? storyBgM : storyBgD} alt="our story" className={css.storyBgImage} />
            <img src={storyBgFold} className={css.storyBgImageFold} alt="fold background" />
          </section>
          <section className={css.whyUs}>
            <h2 className={css.secondaryTitle}>{translate('aboutUs.whyUsTitle')}</h2>
            <div className={css.content}>
              <div className={css.text}>
                <p dangerouslySetInnerHTML={{ __html: translate('aboutUs.whyUsText1') }} />
                <p dangerouslySetInnerHTML={{ __html: translate('aboutUs.whyUsText2') }} />
                <p dangerouslySetInnerHTML={{ __html: translate('aboutUs.whyUsText3') }} />
              </div>
              <div className={css.other}>
                <div className={css.quote}>
                  <p>{`"${translate('aboutUs.quote')}"`}</p>
                  <div
                    className={css.quoteAuthor}
                    dangerouslySetInnerHTML={{ __html: translate('aboutUs.quoteAuthor') }}
                  />
                </div>
                <div className={css.logos}>
                  <img src={cukiermanLogo} alt="cukierman & co logo" className={css.cukiermanLogo} />
                  <img src={catalistLogo} alt="Catalyst Investors Club" className={css.catalistLogo} />
                </div>
              </div>
            </div>
          </section>
          <section className={css.values} style={{ backgroundImage: `url(${isMobile ? valuesBgM : valuesBgD})` }}>
            <h2 className={css.secondaryTitle}>{translate('aboutUs.ourValuesTitle')}</h2>
            <div className={css.list}>
              <div className={css.listItem}>
                <h6 className={css.title}>{translate('aboutUs.ourValuesOption1Title')}</h6>
                <p>{translate('aboutUs.ourValuesOption1Text')}</p>
              </div>
              <div className={css.listItem}>
                <h6 className={css.title}>{translate('aboutUs.ourValuesOption2Title')}</h6>
                <p>{translate('aboutUs.ourValuesOption2Text')}</p>
              </div>
              <div className={css.listItem}>
                <h6 className={css.title}>{translate('aboutUs.ourValuesOption3Title')}</h6>
                <p>{translate('aboutUs.ourValuesOption3Text')}</p>
              </div>
              <div className={css.listItem}>
                <h6 className={css.title}>{translate('aboutUs.ourValuesOption4Title')}</h6>
                <p>{translate('aboutUs.ourValuesOption4Text')}</p>
              </div>
            </div>
          </section>
          <section className={css.team}>
            <h2 className={css.secondaryTitle}>{translate('aboutUs.teamTitle')}</h2>
            <div className={css.grid}>
              {TEAM.map((member, index) => (
                <div key={`member-${index}`} className={css.gridItem}>
                  <div className={css.photo}>
                    <img src={member.image} alt={member.name} title={member.name} />
                  </div>
                  <p className={css.name}>{member.name}</p>
                  <p className={css.position}>{member.position}</p>
                </div>
              ))}
            </div>
          </section>
          <section className={css.board}>
            <h2 className={css.secondaryTitle}>{translate('aboutUs.boardTitle')}</h2>
            <div className={css.grid}>
              {BOARD.map((member, index) => (
                <div key={`board-member-${index}`} className={css.gridItem}>
                  <div className={css.photo}>
                    <img src={member.image} alt={member.name} title={member.name} />
                  </div>
                  <p className={css.name}>{member.name}</p>
                </div>
              ))}
            </div>
          </section>
        </div>
      </div>
    </>
  );
};

export default About;
